import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  User,
  Phone as PhoneIcon,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Building2,
  UtensilsCrossed,
  Users,
  Briefcase,
  Loader2,
} from "lucide-react";
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";
import useHalalFeature from "../hooks/useHalalFeature";
import { hasSolutionsProject } from "../services/solutionsApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { getSafeRedirect, buildRedirectQuery } from "../utils/safeRedirect";
import { oauthLabel, describeOAuthError, splitDisplayName } from "../utils/oauth";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

const API_URL = import.meta.env.VITE_API_URL;
const steps = ["Account", "Verify", "Role"];

// US numbers get the familiar grouping. Anything on another country code is
// left as plain digits behind the plus, because the grouping rules differ per
// country and guessing them wrong is worse than not grouping at all. E.164
// allows 15 digits, which is the cap for both.
const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (!digits.startsWith("1")) return `+${digits.slice(0, 15)}`;

  const l = digits.slice(0, 11);
  if (l.length <= 1) return `+${l}`;
  if (l.length <= 4) return `+${l.slice(0, 1)} (${l.slice(1)}`;
  if (l.length <= 7) return `+${l.slice(0, 1)} (${l.slice(1, 4)}) ${l.slice(4)}`;
  return `+${l.slice(0, 1)} (${l.slice(1, 4)}) ${l.slice(4, 7)}-${l.slice(7)}`;
};

const cleanPhoneNumber = (value) => value.replace(/\D/g, "");

// The field is US only, so the country code is given rather than asked for.
// Backspacing through the rest of the number stops at "+1" instead of emptying
// the field, which would otherwise leave someone re-typing a digit they were
// never asked to supply.
// The field opens on "+1" because nearly every signup is US, but it is an
// ordinary editable value: clearing it and typing another country code works,
// and clearing it entirely brings the example number back.
const PHONE_PREFIX = "+1";

// Autofill hands over whatever the phone or browser profile has stored, which
// for a US contact is usually the 10 digit national number with no country
// code. Read literally that is "+5 (035) 551-234", which then fails validation.
// A bare 10 digits with no leading plus is taken as US. The plus is what marks
// a deliberate country code, so a 10 digit number typed as "+7911123456" is
// left alone.
const normalizePhoneNumber = (value) => {
  const digits = cleanPhoneNumber(value);
  const looksNational = !value.trim().startsWith("+") && digits.length === 10;
  return formatPhoneNumber(looksNational ? `1${digits}` : digits);
};

// US numbers are 11 digits including the country code. Elsewhere the length
// varies, so the check is only that it falls inside the E.164 range.
const isValidPhoneNumber = (value) => {
  const digits = cleanPhoneNumber(value);
  if (digits.startsWith("1")) return digits.length === 11;
  return digits.length >= 8 && digits.length <= 15;
};

const inputClass =
  "h-12 rounded-xl border pl-10 text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]";

/* ─── Role card (editorial restyle) ─── */
function RoleCard({ icon: Icon, title, description, ar, selected, onClick, disabled, badge }) {
  const palette = useEditorialPalette();
  return (
    <button
      type="button"
      className={cn(
        "group relative w-full cursor-pointer rounded-2xl p-5 text-left transition-all duration-300",
        disabled && "cursor-not-allowed opacity-50"
      )}
      style={{
        background: selected ? `${BRAND.gold}10` : palette.ivory,
        border: `1px solid ${selected ? BRAND.gold : palette.hairline}`,
        boxShadow: selected
          ? `0 4px 24px -6px rgba(191,154,47,0.25)`
          : "0 1px 3px rgba(26,23,19,0.04)",
      }}
      onClick={disabled ? undefined : onClick}
    >
      <div className="flex items-center gap-4">
        <div
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl transition-all duration-300"
          style={{
            background: selected ? BRAND.gold : palette.paper,
            color: selected ? "#fff" : palette.ink2,
            border: `1px solid ${selected ? BRAND.gold : palette.hairline}`,
          }}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="flex items-baseline gap-2"
            style={{ fontFamily: EDITORIAL_FONT.serif }}
          >
            <h3
              style={{
                fontSize: 18,
                fontWeight: 500,
                letterSpacing: "-0.01em",
                color: palette.ink,
              }}
            >
              {title}
            </h3>
            {ar && (
              <span
                style={{
                  fontFamily: EDITORIAL_FONT.arabic,
                  fontSize: 14,
                  color: BRAND.gold,
                  direction: "rtl",
                }}
              >
                {ar}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm" style={{ color: palette.muted }}>
            {description}
          </p>
        </div>
        {badge ? (
          <span
            className="flex-shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase"
            style={{
              letterSpacing: "0.12em",
              background: palette.hairline,
              color: palette.muted,
            }}
          >
            {badge}
          </span>
        ) : (
          <div
            className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-all"
            style={{
              background: selected ? BRAND.gold : "transparent",
              border: selected ? "none" : `2px solid ${palette.hairline}`,
            }}
          >
            {selected && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                <path d="M1 5l2.5 2.5L9 2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

export default function SignupFlow() {
  const palette = useEditorialPalette();
  const inputStyle = {
    background: palette.ivory,
    borderColor: palette.hairline,
    color: palette.ink,
    fontFamily: EDITORIAL_FONT.sans,
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: palette.muted,
    fontFamily: EDITORIAL_FONT.sans,
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshProfile, loginWithGoogle, loginWithApple } = useAuth();
  // Optional post-signup redirect (e.g. /signup?redirect=/solutions).
  // Only honored for internal paths to guard against open redirects.
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const safeRedirect = getSafeRedirect(redirectParam);
  const redirectQuery = buildRedirectQuery(safeRedirect);
  // Someone who started on a Solutions page and was sent here to make an
  // account is telling us what they are before they reach the role step. They
  // have no solutionsClients record yet, so the lookup below cannot find them,
  // and without this they would be asked to classify themselves and land on the
  // default. Still a preselection, not a lock: any other card can be picked.
  const arrivedFromSolutions = Boolean(safeRedirect?.startsWith("/solutions"));
  // One flag covers both Google and Apple: the difference is only which popup
  // was opened and what the button says. Both skip the password fields and
  // finish through the same google-signin-complete endpoint.
  const initialGoogleSignup = Boolean(location.state?.googleSignup);
  const initialOauthProvider = location.state?.oauthProvider || "google";
  const googleFirstName = location.state?.firstName || "";
  const googleLastName = location.state?.lastName || "";
  const googleEmail = location.state?.email || "";
  const [googleSignup, setGoogleSignup] = useState(initialGoogleSignup);
  const [oauthProvider, setOauthProvider] = useState(initialOauthProvider);
  const [existingUserUid, setExistingUserUid] = useState(
    location.state?.existingUserUid || null
  );
  // Apple hid the real address behind a per-app relay, so we could not tell
  // whether this person already has an account under their own email.
  const [privateRelay, setPrivateRelay] = useState(
    Boolean(location.state?.privateRelay)
  );

  const startStep = location.state?.startAtStep ?? 0;
  const prefilledEmail = googleEmail || location.state?.email || "";

  const [activeStep, setActiveStep] = useState(startStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [accountData, setAccountData] = useState({
    firstName: googleFirstName || "",
    lastName: googleLastName || "",
    phone: PHONE_PREFIX,
    email: prefilledEmail,
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [verificationStatus, setVerificationStatus] = useState({
    checking: false,
    verified: false,
    resendCooldown: 0,
  });

  const [selectedRole, setSelectedRole] = useState(
    arrivedFromSolutions ? "solutions_customer" : "user"
  );
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [solutionsDetected, setSolutionsDetected] = useState(false);

  // Global halal feature flag: hides the halal role while halal is turned off
  const { halalEnabled } = useHalalFeature();

  useEffect(() => {
    setError("");
  }, [activeStep]);

  // Preselect Solutions for someone who already has a project with us, so an
  // existing customer is never asked to classify themselves as a masjid admin
  // or a halal owner just to reach their portal.
  useEffect(() => {
    let cancelled = false;
    const detect = async () => {
      if (activeStep !== 2 || !auth.currentUser) return;
      const isCustomer = await hasSolutionsProject();
      if (cancelled || !isCustomer) return;
      setSolutionsDetected(true);
      setSelectedRole("solutions_customer");
    };
    detect();
    return () => {
      cancelled = true;
    };
  }, [activeStep]);

  useEffect(() => {
    const loadUserData = async () => {
      // startStep 2 resumes an unfinished signup. googleSignup covers a repeat
      // Apple sign-in: Apple sends the name only on the first authorization, so
      // the fields arrive empty on every later attempt even though we already
      // hold the name — and validation then blocks on details the person has
      // given us before.
      if (!(startStep === 2 || googleSignup) || !auth.currentUser) return;
      try {
        const idToken = await auth.currentUser.getIdToken();
        const response = await fetch(`${API_URL}/v1/users/me`, {
          method: "GET",
          headers: { Authorization: `Bearer ${idToken}` },
        });

        if (response.ok) {
          const data = await response.json();
          const userData = data.user || {};
          // Fills blanks only. Whatever the provider just handed us, or the
          // person has already typed, wins over the stored copy.
          setAccountData((prev) => ({
            ...prev,
            firstName: prev.firstName || userData.firstName || "",
            lastName: prev.lastName || userData.lastName || "",
            phone:
              prev.phone && prev.phone !== PHONE_PREFIX ?
                prev.phone :
                (userData.phone ? formatPhoneNumber(userData.phone) : PHONE_PREFIX),
            email: prev.email || userData.email || auth.currentUser.email || "",
          }));
        }
      } catch (err) {
        console.error("Error loading user data:", err);
      }
    };

    loadUserData();
  }, [startStep, googleSignup]);

  useEffect(() => {
    const preventBackNavigation = () => {
      window.history.pushState(
        null,
        "",
        window.location.pathname + window.location.search
      );
    };
    preventBackNavigation();
    window.addEventListener("popstate", preventBackNavigation);
    return () => window.removeEventListener("popstate", preventBackNavigation);
  }, []);

  useEffect(() => {
    let interval;
    if (activeStep === 1 && !verificationStatus.verified) {
      const checkVerification = async () => {
        try {
          setVerificationStatus((prev) => ({ ...prev, checking: true }));
          await auth.currentUser?.reload();
          const isVerified = auth.currentUser?.emailVerified || false;

          if (isVerified) {
            setVerificationStatus((prev) => ({
              ...prev,
              verified: true,
              checking: false,
            }));
            clearInterval(interval);
            setTimeout(() => {
              setError("");
              setActiveStep(2);
            }, 3000);
          } else {
            setVerificationStatus((prev) => ({ ...prev, checking: false }));
          }
        } catch (err) {
          console.error("Error checking verification:", err);
          setVerificationStatus((prev) => ({ ...prev, checking: false }));
        }
      };
      checkVerification();
      interval = setInterval(checkVerification, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeStep, verificationStatus.verified]);

  useEffect(() => {
    let timer;
    if (verificationStatus.resendCooldown > 0) {
      timer = setTimeout(() => {
        setVerificationStatus((prev) => ({
          ...prev,
          resendCooldown: prev.resendCooldown - 1,
        }));
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [verificationStatus.resendCooldown]);

  const validateStep = () => {
    setError("");

    if (activeStep === 0) {
      if (googleSignup) {
        if (!accountData.firstName || !accountData.lastName || !accountData.phone || !accountData.email) {
          setError("Please fill in all fields");
          return false;
        }
        if (accountData.firstName.length < 2 || accountData.lastName.length < 2) {
          setError("Names must be at least 2 characters long");
          return false;
        }
        if (!isValidPhoneNumber(accountData.phone)) {
          setError("Please enter a valid phone number, including the country code");
          return false;
        }
      } else {
        if (!accountData.firstName || !accountData.lastName || !accountData.phone || !accountData.email || !accountData.password) {
          setError("Please fill in all fields");
          return false;
        }
        if (accountData.firstName.length < 2 || accountData.lastName.length < 2) {
          setError("Names must be at least 2 characters long");
          return false;
        }
        if (!isValidPhoneNumber(accountData.phone)) {
          setError("Please enter a valid phone number, including the country code");
          return false;
        }
        if (accountData.password.length < 6) {
          setError("Password must be at least 6 characters");
          return false;
        }
        if (accountData.password !== accountData.confirmPassword) {
          setError("Passwords do not match");
          return false;
        }
      }

      if (!agreedToTerms) {
        setError("You must agree to the Terms of Service and Privacy Policy to continue");
        return false;
      }
    }

    if (activeStep === 1) {
      if (!verificationStatus.verified) {
        setError("Please verify your email before continuing");
        return false;
      }
    }

    if (activeStep === 2) {
      if (!selectedRole) {
        setError("Please select a role");
        return false;
      }
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    setError("");

    if (activeStep === 0) {
      if (googleSignup) {
        setLoading(true);
        try {
          const idToken = await auth.currentUser.getIdToken();
          const response = await fetch(`${API_URL}/v1/users/google-signin-complete`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              phone: cleanPhoneNumber(accountData.phone),
              firstName: accountData.firstName.trim(),
              lastName: accountData.lastName.trim(),
              existingUserUid,
            }),
          });

          if (!response.ok) throw new Error("BACKEND_ERROR");

          setActiveStep(2);
        } catch (err) {
          console.error("Error saving Google user data:", err);
          setError("An error has occurred. Please try again.");
        } finally {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const signInMethods = await fetchSignInMethodsForEmail(auth, accountData.email);
        if (signInMethods.length > 0) {
          setError("An account with this email already exists. Please sign in or reset your password.");
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          accountData.email,
          accountData.password
        );

        const idToken = await userCredential.user.getIdToken();
        const response = await fetch(`${API_URL}/v1/users/create-initial`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            email: accountData.email.toLowerCase(),
            firstName: accountData.firstName.trim(),
            lastName: accountData.lastName.trim(),
            phone: cleanPhoneNumber(accountData.phone),
          }),
        });

        if (!response.ok) throw new Error("BACKEND_ERROR");

        await sendEmailVerification(userCredential.user, {
          url: window.location.origin + "/signup" + redirectQuery,
          handleCodeInApp: false,
        });

        setActiveStep((prev) => prev + 1);
      } catch (err) {
        console.error("Signup error:", err);
        if (err.code === "auth/email-already-in-use") {
          setError("An account with this email already exists. Please sign in or reset your password.");
        } else if (err.code === "auth/invalid-email") {
          setError("Invalid email address");
        } else if (err.code === "auth/weak-password") {
          setError("Password is too weak. Please use a stronger password.");
        } else {
          setError("An error has occurred. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleResendVerification = async () => {
    if (verificationStatus.resendCooldown > 0) return;
    try {
      if (!auth.currentUser) {
        setError("Session expired. Please try signing up again.");
        return;
      }
      await sendEmailVerification(auth.currentUser, {
        url: window.location.origin + "/signup" + redirectQuery,
        handleCodeInApp: false,
      });
      setVerificationStatus((prev) => ({ ...prev, resendCooldown: 60 }));
      setError("");
    } catch (err) {
      console.error("Resend verification error:", err);
      if (err.code === "auth/too-many-requests") {
        setError("Too many requests. Please wait before trying again.");
      } else {
        setError("Failed to resend verification email. Please try again.");
      }
    }
  };

  const handleRoleSelection = async () => {
    if (!validateStep()) return;
    // Guard against the flag flipping off after the role was picked
    if (selectedRole === "restaurant_admin" && !halalEnabled) {
      setError("Halal business signups are coming soon. Please pick another option.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      if (!auth.currentUser) {
        setError("Session expired. Please try signing up again.");
        setLoading(false);
        return;
      }

      const idToken = await auth.currentUser.getIdToken(true);
      // Muslim+ Solutions is NOT an app role. A Solutions customer gets an
      // ordinary community account, and their access to the portal comes from
      // the solutionsClients record matched on their verified email. That keeps
      // the two systems independent while sharing one Firebase Auth identity,
      // and lets a Solutions customer also be a masjid admin later without
      // either role fighting the other.
      const signupTypeMap = {
        user: "community",
        masjid_admin: "masjid",
        restaurant_admin: "restaurant",
        solutions_customer: "community",
      };
      const signupType = signupTypeMap[selectedRole];
      const isSolutionsCustomer = selectedRole === "solutions_customer";

      const response = await fetch(`${API_URL}/v1/users/complete-signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          signupType,
          firstName: accountData.firstName,
          lastName: accountData.lastName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === "EMAIL_NOT_VERIFIED") {
          setError("Email not verified. Please verify your email first.");
          setActiveStep(1);
        } else if (data.code === "ALREADY_COMPLETE") {
          await refreshProfile();
          if (isSolutionsCustomer) {
            navigate(safeRedirect || "/solutions/portal");
          } else if (data.role === "halal" || data.role === "restaurant") {
            navigate("/halal-signup");
          } else if (data.role === "masjid") {
            navigate("/masjid-dashboard");
          } else {
            navigate(safeRedirect || "/");
          }
        } else {
          throw new Error("BACKEND_ERROR");
        }
        setLoading(false);
        return;
      }

      // complete-signup changes the profile server-side, and no auth state
      // change follows it, so pull the fresh profile before navigating or the
      // nav keeps showing "Complete signup" until a manual reload.
      await refreshProfile();

      if (isSolutionsCustomer) {
        navigate(safeRedirect || "/solutions/portal");
      } else if (data.role === "halal" || data.role === "restaurant") {
        navigate("/halal-signup");
      } else if (data.role === "masjid") {
        navigate("/masjid-signup");
      } else {
        navigate(safeRedirect || "/");
      }
    } catch (err) {
      console.error("Role selection error:", err);
      setError("An error has occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignupStart = async (providerId) => {
    const isApple = providerId === "apple";
    const label = oauthLabel(providerId);
    setError("");
    setLoading(true);
    try {
      // Goes through the same context helper the login page uses, rather than
      // opening its own popup: that is what runs google-signin-check, so
      // someone who already has an account is recognised here too. Doing the
      // popup directly skipped the check entirely and passed this user's own
      // uid as existingUserUid, which the backend ignores — so arriving at
      // signup instead of login quietly cost you your existing account.
      const result = isApple ? await loginWithApple() : await loginWithGoogle();
      const user = result.user;
      // Apple returns the name only on the first authorization, so this can be
      // empty on a repeat signup attempt; the fields stay editable either way.
      const { firstName, lastName } = splitDisplayName(user?.displayName);

      // Already a finished account: nothing to sign up for.
      if (result.needsSignup === false && result.signupComplete === true) {
        navigate(safeRedirect || "/");
        return;
      }

      setGoogleSignup(true);
      setOauthProvider(providerId);
      setExistingUserUid(result.existingUser?.uid || null);
      setPrivateRelay(!!result.privateRelay);
      setAccountData((prev) => ({
        ...prev,
        firstName: prev.firstName || firstName,
        lastName: prev.lastName || lastName,
        email: user?.email || prev.email,
      }));
      setActiveStep(0);
    } catch (err) {
      if (!err?.code) console.error(`${label} signup start error:`, err);
      setError(describeOAuthError(err, label));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activeStep === 1) return;
    if (activeStep === steps.length - 1) {
      await handleRoleSelection();
    } else {
      await handleNext();
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div className="space-y-4">
            {privateRelay && (
              <div
                className="rounded-xl px-4 py-3 text-sm"
                style={{
                  background: BRAND.gold + "18",
                  border: `1px solid ${BRAND.gold}55`,
                  color: palette.ink2,
                  lineHeight: 1.55,
                }}
              >
                Apple is hiding your email address, so we cannot tell whether you already
                have a Muslim+ account. If you do, go back and sign in the way you did
                before — continuing here creates a second, separate account.
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="firstName" style={labelStyle}>
                  First name
                </Label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: palette.muted }} />
                  <Input
                    id="firstName"
                    className={inputClass}
                    style={inputStyle}
                    placeholder="John"
                    autoComplete="given-name"
                    value={accountData.firstName}
                    onChange={(e) => setAccountData({ ...accountData, firstName: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName" style={labelStyle}>
                  Last name
                </Label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: palette.muted }} />
                  <Input
                    id="lastName"
                    className={inputClass}
                    style={inputStyle}
                    placeholder="Doe"
                    autoComplete="family-name"
                    value={accountData.lastName}
                    onChange={(e) => setAccountData({ ...accountData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone" style={{ ...labelStyle, color: googleSignup ? BRAND.gold : palette.muted }}>
                Phone number {googleSignup && <span style={{ textTransform: "none", letterSpacing: "normal", fontWeight: 500 }}>(required)</span>}
              </Label>
              <div className="relative">
                <PhoneIcon
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2"
                  style={{ color: googleSignup ? BRAND.gold : palette.muted }}
                />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  className={inputClass}
                  style={{
                    ...inputStyle,
                    borderColor: googleSignup ? BRAND.gold : palette.hairline,
                  }}
                  value={accountData.phone}
                  onChange={(e) => setAccountData({ ...accountData, phone: normalizePhoneNumber(e.target.value) })}
                  placeholder="+1 (555) 123-4567"
                  required
                  autoFocus={googleSignup}
                />
                {/* The field opens holding "+1", so the native placeholder is
                    suppressed and the example number would vanish before the
                    user has typed anything. This draws the rest of the example
                    after the real "+1", using a hidden copy of it as a spacer so
                    the two align in whatever font the browser resolves. It goes
                    as soon as there is anything to read. */}
                {accountData.phone === PHONE_PREFIX && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-[15px]"
                    style={{ fontFamily: EDITORIAL_FONT.sans, color: palette.muted }}
                  >
                    <span style={{ visibility: "hidden" }}>{PHONE_PREFIX}</span>
                    {" (555) 123-4567"}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" style={labelStyle}>
                Email address
              </Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: palette.muted }} />
                <Input
                  id="email"
                  type="email"
                  className={`${inputClass} disabled:opacity-60`}
                  style={inputStyle}
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={accountData.email}
                  onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
                  required
                  // Locked only when there is actually an address to lock. A
                  // provider that returns no email otherwise left this both
                  // empty and uneditable, and validation demands it non-empty,
                  // so the step could not be completed at all.
                  disabled={(startStep === 1 || googleSignup) && !!accountData.email}
                />
              </div>
            </div>

            {!googleSignup && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="password" style={labelStyle}>
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: palette.muted }} />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      className={`${inputClass} pr-11`}
                      style={inputStyle}
                      placeholder="Min. 6 characters"
                      autoComplete="new-password"
                      value={accountData.password}
                      onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors hover:opacity-80"
                      style={{ color: palette.muted }}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" style={labelStyle}>
                    Confirm password
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: palette.muted }} />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      className={`${inputClass} pr-11`}
                      style={inputStyle}
                      placeholder="Re-enter password"
                      autoComplete="new-password"
                      value={accountData.confirmPassword}
                      onChange={(e) => setAccountData({ ...accountData, confirmPassword: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors hover:opacity-80"
                      style={{ color: palette.muted }}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                className="mt-0.5"
                style={{
                  borderColor: palette.hairline,
                }}
              />
              <Label
                htmlFor="terms"
                className="cursor-pointer text-sm leading-relaxed"
                style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
              >
                I agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: BRAND.gold, fontWeight: 600, textDecoration: "underline" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="/policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: BRAND.gold, fontWeight: 600, textDecoration: "underline" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </Label>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="flex flex-col items-center justify-center py-4">
            {startStep === 1 && (
              <div
                className="mb-6 w-full rounded-xl p-4 text-center text-sm"
                style={{
                  background: BRAND.gold + "10",
                  border: `1px solid ${BRAND.gold}40`,
                  color: BRAND.goldDeep,
                }}
              >
                Please verify your email before continuing. Check your inbox for the verification link.
              </div>
            )}

            <div className="relative mb-6">
              {!verificationStatus.verified && (
                <div className="pointer-events-none absolute -inset-4">
                  <div className="absolute inset-0 rounded-full border" style={{ borderColor: BRAND.gold + "30" }} />
                  <div
                    className="absolute inset-0 animate-[spin_1.6s_linear_infinite] rounded-full border-2 border-transparent"
                    style={{ borderTopColor: BRAND.gold }}
                  />
                </div>
              )}
            </div>

            <SerifHeading
              level={3}
              className="mb-2"
              style={{ fontSize: 26, lineHeight: 1 }}
            >
              {verificationStatus.verified ? (
                <>
                  Email <em style={{ color: BRAND.gold, fontWeight: 400 }}>verified.</em>
                </>
              ) : (
                <>
                  Check your <em style={{ color: BRAND.gold, fontWeight: 400 }}>inbox.</em>
                </>
              )}
            </SerifHeading>

            <p className="mb-4 max-w-[320px] text-center text-sm leading-relaxed" style={{ color: palette.muted }}>
              {verificationStatus.verified
                ? "Your email has been verified. Proceeding to the next step…"
                : startStep === 1
                ? "You need to verify your email to continue:"
                : "We've sent a verification email to:"}
            </p>

            <div
              className="mb-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
              style={{
                background: palette.ivory,
                border: `1px solid ${palette.hairline}`,
                color: palette.ink,
              }}
            >
              <Mail className="h-3.5 w-3.5" style={{ color: palette.muted }} />
              {accountData.email}
            </div>

            {!verificationStatus.verified && (
              <>
                <p className="mb-5 max-w-[320px] text-center text-sm leading-relaxed" style={{ color: palette.muted }}>
                  Click the link in the email to verify your account. This page will automatically
                  update when verified.
                </p>

                <PillButton
                  variant="outline"
                  onClick={handleResendVerification}
                  disabled={verificationStatus.resendCooldown > 0}
                  className="w-full max-w-[320px] justify-center"
                >
                  {verificationStatus.resendCooldown > 0
                    ? `Resend in ${verificationStatus.resendCooldown}s`
                    : "Resend verification email"}
                </PillButton>

                <p className="mt-3 text-xs" style={{ color: palette.muted }}>
                  Check your spam folder if you don't see the email.
                </p>
              </>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-3">
            <p className="mb-5 text-center text-sm" style={{ color: palette.muted }}>
              What best describes you?
            </p>

            {solutionsDetected && (
              <p
                className="mb-4 rounded-xl px-4 py-3 text-center text-sm"
                style={{
                  background: BRAND.gold + "18",
                  border: `1px solid ${BRAND.gold}55`,
                  color: palette.ink2,
                  lineHeight: 1.55,
                }}
              >
                We found a Muslim+ Solutions project for this email, so we have selected
                that for you. You can still pick another option if you also use Muslim+ in
                a different way.
              </p>
            )}

            <RoleCard
              icon={Briefcase}
              title="Solutions customer"
              ar="الحلول"
              description="Manage your project, billing, and change requests"
              selected={selectedRole === "solutions_customer"}
              onClick={() => setSelectedRole("solutions_customer")}
            />

            <RoleCard
              icon={Users}
              title="Community member"
              ar="الأمة"
              description="Find masjids, prayer times, and events"
              selected={selectedRole === "user"}
              onClick={() => setSelectedRole("user")}
            />

            <RoleCard
              icon={Building2}
              title="Masjid administrator"
              ar="المسجد"
              description="List and manage your masjid"
              selected={selectedRole === "masjid_admin"}
              onClick={() => setSelectedRole("masjid_admin")}
            />

            {/* Halal signups are only selectable while the halal feature is live */}
            <RoleCard
              icon={UtensilsCrossed}
              title="Halal owner"
              ar="حلال"
              description={
                halalEnabled
                  ? "List and manage your halal business"
                  : "Halal listings open soon"
              }
              selected={halalEnabled && selectedRole === "restaurant_admin"}
              onClick={() => setSelectedRole("restaurant_admin")}
              disabled={!halalEnabled}
              badge={halalEnabled ? null : "Coming soon"}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <EditorialShell>
      <div className="flex min-h-screen flex-col lg:h-screen lg:flex-row">
        {/* ════════════ LEFT PANEL ════════════ */}
        <div
          className="relative hidden overflow-hidden lg:flex lg:h-screen lg:w-[45%] xl:w-[42%] flex-col p-10 xl:p-14"
          style={{ background: palette.paper }}
        >
          <PatternTile opacity={0.06} />

          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Muslim+ home"
            className="relative z-10 flex items-center gap-2.5 self-start transition-opacity hover:opacity-80"
          >
            <span
              style={{
                fontFamily: EDITORIAL_FONT.sans,
                fontSize: 24,
                fontWeight: 500,
                lineHeight: 1,
                letterSpacing: "-0.015em",
                color: palette.ink,
              }}
            >
              Muslim+
            </span>
          </button>

          <div className="relative z-10 flex flex-1 items-center">
            <div>
              <div
                className="mb-5 inline-flex items-center gap-2.5 text-[11px] sm:text-xs"
                style={{
                  fontFamily: EDITORIAL_FONT.sans,
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: BRAND.gold,
                }}
              >
                Join the community
              </div>
              <SerifHeading
                level={1}
                className="mt-2"
                style={{
                  fontSize: "clamp(2.75rem, 4.5vw, 4.25rem)",
                  lineHeight: 1,
                  letterSpacing: "-0.035em",
                }}
              >
                Your deen,
                <br />
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>one app.</em>
              </SerifHeading>
              <p
                className="mt-6 max-w-md text-[15px]"
                style={{ color: palette.ink2, lineHeight: 1.55 }}
              >
                Prayer times, masajid, events, and halal. Built for the ummah.
              </p>
            </div>
          </div>
        </div>

        {/* ════════════ RIGHT PANEL — form ════════════ */}
        <div
          className="relative flex flex-1 flex-col lg:h-screen lg:overflow-y-auto"
          style={{ background: palette.ivory }}
        >
          {/* Mobile top bar */}
          <div className="flex items-center justify-between px-5 pt-6 lg:hidden">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2.5"
              aria-label="Muslim+ home"
            >
              <span
                style={{
                  fontFamily: EDITORIAL_FONT.sans,
                  fontSize: 22,
                  fontWeight: 500,
                  lineHeight: 1,
                  letterSpacing: "-0.015em",
                  color: palette.ink,
                }}
              >
                Muslim+
              </span>
            </button>
          </div>

      <button
        onClick={() => navigate(arrivedFromSolutions ? safeRedirect : "/")}
        className="absolute right-10 top-10 z-10 hidden whitespace-nowrap text-sm transition-colors hover:opacity-70 lg:inline-block"
        style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
      >
        ← Back to {arrivedFromSolutions ? "Solutions" : "home"}
      </button>
      <div className="flex flex-1 justify-center px-5 pt-6 pb-10 sm:px-10 sm:pt-8 lg:px-16 lg:pt-24">
        <div className="w-full max-w-[520px]" style={{ animation: "editorialFadeUp 0.5s ease-out both" }}>
          {/* Step progress */}
          <div className="mb-6">
            <div className="flex items-center gap-2">
              {steps.map((label, index) => (
                <div key={label} className="flex flex-1 flex-col items-center gap-2">
                  <div
                    className="h-[2px] w-full rounded-full transition-all duration-500"
                    style={{
                      background:
                        index < activeStep
                          ? BRAND.gold
                          : index === activeStep
                          ? BRAND.gold + "99"
                          : palette.hairline,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: EDITORIAL_FONT.sans,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: index <= activeStep ? BRAND.gold : palette.muted,
                    }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Card */}
          <EditorialCard
            variant="paper"
            className="relative overflow-hidden p-5 sm:p-7"
            style={{
              borderRadius: 20,
              borderColor: palette.isDark ? "#3a322a" : palette.hairline,
              boxShadow: palette.isDark
                ? "0 24px 60px -24px rgba(0,0,0,0.6)"
                : "0 24px 60px -24px rgba(26,23,19,0.15)",
            }}
          >
            <div className="relative">
              {error && (
                <div
                  className="mb-5 rounded-xl p-3.5 text-sm font-medium"
                  style={{
                    background: BRAND.terracotta + "15",
                    border: `1px solid ${BRAND.terracotta}40`,
                    color: BRAND.terracotta,
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="min-h-[300px]">{renderStepContent()}</div>

                {activeStep !== 1 && (
                  <div className="mt-5 space-y-3">
                    <PillButton
                      type="submit"
                      variant="primary"
                      disabled={loading}
                      className="w-full justify-center"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {activeStep === 0 ? (googleSignup ? "Saving…" : "Creating account…") : "Completing…"}
                        </>
                      ) : (
                        <>
                          {activeStep === 0 ? (googleSignup ? "Continue" : "Create account") : "Complete signup"}
                          <span aria-hidden>→</span>
                        </>
                      )}
                    </PillButton>

                    {activeStep === 0 && !googleSignup && (
                      <>
                        <div className="flex items-center gap-3 py-1">
                          <div className="h-px flex-1" style={{ background: palette.hairline }} />
                          <span
                            className="text-xs font-medium uppercase"
                            style={{ letterSpacing: "0.14em", color: palette.muted }}
                          >
                            or
                          </span>
                          <div className="h-px flex-1" style={{ background: palette.hairline }} />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-12 w-full gap-3 rounded-full text-sm font-semibold transition-all"
                          onClick={() => handleOAuthSignupStart("google")}
                          disabled={loading}
                          style={{
                            background: "#ffffff",
                            borderColor: "#dadce0",
                            color: "#1a1713",
                            fontFamily: EDITORIAL_FONT.sans,
                          }}
                        >
                          <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                          </svg>
                          Continue with Google
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-12 w-full gap-3 rounded-full text-sm font-semibold transition-all"
                          onClick={() => handleOAuthSignupStart("apple")}
                          disabled={loading}
                          style={{
                            background: "#000000",
                            borderColor: "#000000",
                            color: "#ffffff",
                            fontFamily: EDITORIAL_FONT.sans,
                          }}
                        >
                          <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                            <path d="M16.365 1.43c0 1.14-.42 2.2-1.12 3.02-.85.99-2.24 1.76-3.4 1.67a3.6 3.6 0 0 1-.03-.42c0-1.1.5-2.24 1.2-3.02.79-.9 2.14-1.58 3.3-1.63.03.13.05.26.05.38zM20.9 17.1c-.57 1.32-.85 1.91-1.58 3.08-1.02 1.63-2.47 3.67-4.26 3.68-1.59.02-2-1.04-4.16-1.02-2.16.01-2.6 1.04-4.2 1.02-1.79-.01-3.16-1.85-4.19-3.48C-.4 16.83-.7 11.5 1.05 8.67c1.24-2.01 3.2-3.19 5.04-3.19 1.87 0 3.05 1.03 4.6 1.03 1.5 0 2.42-1.03 4.58-1.03 1.64 0 3.37.89 4.61 2.43-4.05 2.22-3.4 8-.98 9.19z" />
                          </svg>
                          Continue with Apple
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {activeStep === 0 && !googleSignup && (
                  <p className="mt-5 text-center text-sm" style={{ color: palette.muted }}>
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => navigate(`/login${redirectQuery}`)}
                      className="font-semibold transition-colors hover:underline"
                      style={{ color: BRAND.gold, fontFamily: EDITORIAL_FONT.sans }}
                    >
                      Sign in
                    </button>
                  </p>
                )}
                {activeStep === 0 && googleSignup && (
                  <p className="mt-5 text-center text-sm" style={{ color: palette.muted }}>
                    Signed in with {oauthProvider === "apple" ? "Apple" : "Google"} as{" "}
                    <span className="font-semibold" style={{ color: palette.ink }}>{accountData.email}</span>
                  </p>
                )}
              </form>
            </div>
          </EditorialCard>
        </div>
      </div>

          <div className="px-5 pb-6 text-center lg:px-10 lg:pb-8 lg:text-right">
            <p className="text-xs" style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}>
              © {new Date().getFullYear()} Muslim+. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes editorialFadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </EditorialShell>
  );
}
